import React from 'react';

import { fetchLiveTopic } from 'reduxModules/live/action';
import { fetchLiveTopicFlow } from 'reduxModules/live/saga';
import {
  getLiveTopicInfo,
  getLiveTopicSyllabuses,
} from 'reduxModules/live/selector';
import { generateOfficialSiteURL } from 'reduxModules/navigation/selector';
import { HOME, LIVE, LIVE_TOPIC } from 'constants/routingPath';
import { SNAPASK_SITE_ENDPOINT } from 'config';
import {
  createBreadcrumbForJsonLD,
  getOfferInfoForJsonLD,
  getRatingForJsonLD,
} from 'utils/jsonLD';
import * as contentTypes from 'constants/contentTypes';
import {
  getContentCheckoutCollectionList,
  getContentMainCheckoutCollection,
} from 'reduxModules/checkoutCollection/selector';
import {
  fetchCheckoutCollectionListFlow,
  fetchLiveTopicMainCheckoutCollectionFlow,
} from 'reduxModules/checkoutCollection/saga';
import {
  fetchCheckoutCollectionList,
  fetchLiveTopicMainCheckoutCollection,
} from 'reduxModules/checkoutCollection/action';

import LiveTopicSyllabus from './LiveTopicSyllabus';

async function action(context) {
  const { i18n, store, params, pathname } = context;

  await Promise.all([
    store.runSagas(fetchLiveTopicFlow, fetchLiveTopic(params.id)).toPromise(),
    store
      .runSagas(
        fetchLiveTopicMainCheckoutCollectionFlow,
        fetchLiveTopicMainCheckoutCollection(params.id),
      )
      .toPromise(),
    store.runSagas(
      fetchCheckoutCollectionListFlow,
      fetchCheckoutCollectionList({
        contentType: contentTypes.LIVE_TOPIC,
        contentId: params.id,
      }),
    ),
  ]);
  const liveTopicInfo = getLiveTopicInfo(store.getState());
  const syllabusLesson = getLiveTopicSyllabuses(store.getState())?.reduce(
    (result, { syllabusLessons }) =>
      result ||
      syllabusLessons.find(({ id }) => id === Number(params.lessonId)),
    null,
  );

  const {
    id: liveTopicId,
    name: topicName,
    isEnrolled,
    picture,
    instructors,
    metaDescription: topicDescription,
    averageRate,
    ratings,
  } = liveTopicInfo;

  const {
    id: lessonId,
    name: syllabusName,
    metaDescription: syllabusDescription,
  } = syllabusLesson;

  if (!liveTopicId || !lessonId) return null;

  if (isEnrolled) {
    return {
      redirect: generateOfficialSiteURL(store.getState())(LIVE_TOPIC, {
        id: liveTopicId,
      }),
    };
  }

  const mainPaymentOption = getContentMainCheckoutCollection(
    contentTypes.LIVE_TOPIC,
    liveTopicId,
  )(store.getState());

  const contentList = getContentCheckoutCollectionList(
    contentTypes.LIVE_TOPIC,
    liveTopicId,
  )(store.getState());

  const pictureUrl = picture.originalUrl;
  const name = syllabusName || topicName;
  const metaDescription = syllabusDescription || topicDescription;

  const getOfficialSiteURL = generateOfficialSiteURL(store.getState());

  const breadcrumbList = [
    {
      link: getOfficialSiteURL(HOME),
      label: i18n.t('common:common.breadcrumb.home'),
    },
    {
      link: getOfficialSiteURL(LIVE),
      label: i18n.t('navbar:navbar.link.live'),
    },
    {
      link: getOfficialSiteURL(LIVE_TOPIC, { id: liveTopicInfo.id }),
      label: topicName,
    },
  ];

  const currentPage = {
    link: pathname,
    label: syllabusName,
  };

  return {
    title: i18n.t('seo:seo.live.topic.title', {
      topicName: name,
    }),
    description: metaDescription,
    ogTags: {
      title: name,
      description: metaDescription,
      image: pictureUrl,
      type: 'website',
      url: `${SNAPASK_SITE_ENDPOINT}${pathname}`,
    },
    jsonLDList: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        image: pictureUrl,
        name,
        description: metaDescription,
        publisher: {
          '@type': 'Organization',
          name: 'Snapask',
        },
        provider: {
          '@type': 'Person',
          name: instructors[0]?.name,
          image: instructors[0]?.avatarUrl,
        },
        ...getOfferInfoForJsonLD(mainPaymentOption, contentList),
        ...getRatingForJsonLD(averageRate, ratings),
      },
      createBreadcrumbForJsonLD(
        [...breadcrumbList, currentPage],
        'live topic syllabus lesson page',
      ),
    ],
    chunks: ['LiveTopicSyllabus'],
    component: (
      <LiveTopicSyllabus
        liveTopicId={liveTopicId}
        lessonId={+params.lessonId}
        syllabusLesson={syllabusLesson}
      />
    ),
  };
}

export default action;
